import {
  AddMakeupReceipt,
  AddStudentToClassReceipt,
  AddTryoutReceipt,
  EnrollmentReceiptType,
  MakeupReceiptType,
  RECEIPT_ENDPOINTS,
  STORYTIME_ENDPOINT,
  StorytimeEmailRequest,
  SwitchClassReceipt,
  TryoutFollowupPassReceipt,
  TryoutReceiptType,
} from "#customTypes/composedTypes/emailTypes"
import { PrintEvalData } from "#customTypes/composedTypes/evaluationTypes"
import { invariant } from "#customTypes/types"
import { authedFetch } from "#utils/apiFetch"

class ApiHandler {
  constructor() {}

  // TODO: Fill out receipt types
  async sendReceiptEmail(
    receipt:
      | AddStudentToClassReceipt
      | AddTryoutReceipt
      | AddMakeupReceipt
      | SwitchClassReceipt
      | TryoutFollowupPassReceipt,
    type: EnrollmentReceiptType | TryoutReceiptType | MakeupReceiptType
  ): Promise<{ mandrillIds: string[]; sendAt: string | null }> {
    const route = RECEIPT_ENDPOINTS[type]
    console.log("✅🔥 ApiHandler route => ", route)

    const response = (await authedFetch(route, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(receipt),
    })) as Response

    // The server categorizes Mandrill errors: 502 + structured body when
    // Mandrill rejected (auth, validation, rate-limit, network), 500 for
    // truly unexpected exceptions. Surface the server's error message so
    // GenericFormModal's error modal shows the teacher *why* it failed.
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as {
        error?: string
        isAuthError?: boolean
      }
      throw new Error(body.error ?? "Failed to send receipt email")
    }

    const body = (await response.json()) as {
      body?: string
      mandrillIds?: string[]
      sendAt?: string | null
    }

    return {
      mandrillIds: body.mandrillIds ?? [],
      sendAt: body.sendAt ?? null,
    }
  }

  // Class-wide storytime email with audio attachments (JARVIS-116). The
  // server enforces recipient/attachment shape and the Mandrill 25MB message
  // cap; errors surface with the server's categorized message (same pattern
  // as sendReceiptEmail).
  async sendStorytimeEmail(
    request: StorytimeEmailRequest
  ): Promise<{ mandrillIds: string[] }> {
    const response = (await authedFetch(STORYTIME_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    })) as Response

    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as {
        error?: string
      }
      throw new Error(body.error ?? "Failed to send storytime email")
    }

    const body = (await response.json()) as { mandrillIds?: string[] }
    return { mandrillIds: body.mandrillIds ?? [] }
  }

  async cancelScheduledReceipt(
    mandrillIds: string[]
  ): Promise<{ canceled: string[]; alreadyGone: string[] }> {
    if (!mandrillIds.length) return { canceled: [], alreadyGone: [] }

    const response = (await authedFetch(`/api/cancelReceipt`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ mandrillIds }),
    })) as Response

    // The server returns 502 when at least one Mandrill cancelScheduled call
    // fails for a real reason (auth, network, validation). It returns 200 even
    // when ids were "already gone" — those are no-ops, but we still need to
    // tell the caller so the UI can warn that the email was already delivered.
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as {
        error?: string
        failedCount?: number
      }
      throw new Error(
        body.error ??
          `Failed to cancel ${body.failedCount ?? mandrillIds.length} of ${
            mandrillIds.length
          } scheduled email(s)`
      )
    }

    const body = (await response.json().catch(() => ({}))) as {
      results?: Array<{ id: string; status: string }>
    }
    const results = body.results ?? []
    return {
      canceled: results.filter((r) => r.status === "canceled").map((r) => r.id),
      alreadyGone: results
        .filter((r) => r.status === "already_gone")
        .map((r) => r.id),
    }
  }

  // Don't error if this fails. It would just be more annoying.
  async sendErrorToDevs(action: string, error: string, context: string) {
    await authedFetch(`/api/sendErrorToDevs`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ action, error, context }),
    })
  }

  async generateEvalPdf(
    data: PrintEvalData,
    templateId: string
  ): Promise<{ url: string; errors: string[] }> {
    console.log("ApiHandler - generateEvalPdf data => ", data)
    console.log("ApiHandler - generateEvalPdf templateId => ", templateId)

    // Pass the data through directly - let the server handle schema filtering
    const response = await authedFetch(`/api/printEval/${templateId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })

    const body = await response.json()

    // Handle validation errors (422 status)
    if (response.status === 422) {
      const validationErrors = body.details || []
      console.error(
        "Validation errors for ",
        data.studentname,
        validationErrors.join(", ")
      )

      throw new Error(body.details)
    }

    const url = body.submission?.download_url
    const errors = body.submission?.errors || body.errors || []

    if (!response.ok || errors?.length > 0 || !url) {
      console.error(
        "Failed to print eval for ",
        data.studentname,
        errors.join(", ")
      )
      throw new Error(errors)
    }

    return {
      url,
      errors,
    }
  }

  async downloadFileFromUrl(url: string, filename: string): Promise<void> {
    invariant(filename != null, "Filename is required to download file")
    invariant(url != null, "URL is required to download file")

    return new Promise<void>((resolve, reject) => {
      const oReq = new XMLHttpRequest()
      oReq.open("GET", url, true)
      oReq.responseType = "blob"

      oReq.onload = function () {
        if (oReq.status !== 200) {
          reject(
            new Error(`Download failed: ${oReq.status} ${oReq.statusText}`)
          )
          return
        }

        const file = new Blob([oReq.response], {
          type: "application/pdf",
        })

        const fileURL = URL.createObjectURL(file)
        const downloadLink = document.createElement("a")
        downloadLink.href = fileURL
        downloadLink.download = filename

        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)

        URL.revokeObjectURL(fileURL)
        resolve()
      }

      oReq.onerror = function () {
        reject(new Error(`Network error downloading ${filename}`))
      }

      oReq.send()
    })
  }

  async generateEvalPdfBatch(
    evaluations: Array<{
      templateId: string
      filename: string
      data: Record<string, unknown>
    }>
  ): Promise<{
    batch_id: string
    results: Array<{
      id: string
      filename: string
      success: boolean
      url?: string
      error?: string
    }>
    summary: {
      total: number
      successful: number
      failed: number
    }
  }> {
    console.log(
      "ApiHandler - generateEvalPdfBatch evaluations => ",
      evaluations
    )

    const response = await authedFetch(`/api/printEvalBatch`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ evaluations }),
    })

    const body = await response.json()

    if (!response.ok) {
      console.error("Failed to generate batch PDFs:", body)
      throw new Error(body.error || "Batch PDF generation failed")
    }

    return body
  }

  async generateEvalPdfByEvalId(
    evaluationId: string
  ): Promise<{ url: string; filename: string; errors: string[] }> {
    const response = await authedFetch(
      `/api/printEvalByEvalId/${evaluationId}`,
      { method: "POST" }
    )
    const body = await response.json()
    if (!response.ok) {
      throw new Error(body?.error || "Failed to generate PDF")
    }
    return body
  }

  async generateEvalBatchByIds(
    evaluationIds: string[],
    userId?: string
  ): Promise<{ accepted: number }> {
    const response = await authedFetch(`/api/printEvalBatchByIds`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ evaluationIds, userId }),
    })
    const body = await response.json()
    if (!response.ok && response.status !== 202) {
      throw new Error(body?.error || "Batch by IDs failed")
    }
    return body
  }
}

export default ApiHandler
